import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Container from '../layout/Container';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../../utils/scrollUtils';

interface NavbarProps {
  transparent?: boolean;
}

const Navbar = ({ transparent = false }: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <NavbarContainer scrolled={scrolled} transparent={transparent}>
      <Container>
        <NavbarContent>
          <LogoContainer to="/">
            <LogoImage src="/images/ca_wave_logo.png" alt="Christopher Anto" />
            <Logo>Christopher Anto</Logo>
          </LogoContainer>

          <NavItems>
            <NavItemScroll onClick={() => scrollToSection('home')}>Home</NavItemScroll>
            <NavItemScroll onClick={() => scrollToSection('about')}>About</NavItemScroll>
            <NavItemScroll onClick={() => scrollToSection('projects')}>Projects</NavItemScroll>
            <NavItemScroll onClick={() => scrollToSection('contact')}>Contact</NavItemScroll>
          </NavItems>

          <MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
            <HamburgerIcon isOpen={isOpen} />
          </MobileMenuButton>
        </NavbarContent>
      </Container>

      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: 'tween', duration: 0.2 }}
          >
            <MobileNavItems>
              <MobileNavItemScroll onClick={() => {
                setIsOpen(false);
                scrollToSection('home');
              }}>
                Home
              </MobileNavItemScroll>
              <MobileNavItemScroll onClick={() => {
                setIsOpen(false);
                scrollToSection('about');
              }}>
                About
              </MobileNavItemScroll>
              <MobileNavItemScroll onClick={() => {
                setIsOpen(false);
                scrollToSection('projects');
              }}>
                Projects
              </MobileNavItemScroll>
              <MobileNavItemScroll onClick={() => {
                setIsOpen(false);
                scrollToSection('contact');
              }}>
                Contact
              </MobileNavItemScroll>
            </MobileNavItems>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

interface NavbarContainerProps {
  scrolled: boolean;
  transparent: boolean;
}

const NavbarContainer = styled.header<NavbarContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease;
  background-color: ${({ theme, scrolled, transparent }) =>
    scrolled ? theme.colors.background : transparent ? 'transparent' : theme.colors.background};
  backdrop-filter: ${({ scrolled }) => scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${({ theme, scrolled }) => 
    scrolled ? theme.boxShadow.normal : 'none'};
  height: 80px;
  display: flex;
  flex-direction: column;
  
  // Ensure navbar is visible on mobile
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    background-color: ${({ theme }) => theme.colors.background};
    backdrop-filter: blur(10px);
  }
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  margin-right: ${props => props.theme.spacing.sm};
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -2px; /* Fine-tune vertical alignment */
`;

const Logo = styled.h2`
  color: ${props => props.theme.colors.text};
  font-weight: 700;
  font-size: 1.5rem;
  transition: color ${props => props.theme.transitions.fast};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavItems = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavItemScroll = styled.div`
  margin-left: ${props => props.theme.spacing.lg};
  cursor: pointer;
  position: relative;
  font-weight: 500;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
`;

interface HamburgerIconProps {
  isOpen: boolean;
}

const HamburgerIcon = styled.div<HamburgerIconProps>`
  width: 24px;
  height: 18px;
  position: relative;
  transform: rotate(0deg);
  transition: 0.5s ease-in-out;
  cursor: pointer;

  &:before,
  &:after,
  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${props => props.theme.colors.text};
    border-radius: 9px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: 0.25s ease-in-out;
  }

  &:before {
    content: '';
    top: ${props => (props.isOpen ? '8px' : '0')};
    transform: ${props => (props.isOpen ? 'rotate(135deg)' : 'none')};
  }

  &:after {
    content: '';
    top: ${props => (props.isOpen ? '8px' : '16px')};
    transform: ${props => (props.isOpen ? 'rotate(-135deg)' : 'none')};
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  height: auto;
  background-color: ${props => props.theme.colors.background};
  box-shadow: ${props => props.theme.boxShadow.normal};
  z-index: 200;
  padding: 0.5rem 0;
  backdrop-filter: blur(10px);
`;

const MobileNavItems = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0 1rem;
`;

const MobileNavItemScroll = styled.div`
  padding: 0.5rem;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

export default Navbar;
